import { Node } from 'reactflow';
import useSourceNode from '@components/EntityDrawers/hooks/useSourceNode';
import { canvasState } from '@components/MainStage/store';
import { useProject } from '@context/Project/ProjectProvider';

type Options<T> = {
  isNodeVisible: (values: T[], node: Node | undefined) => boolean;
  onSetValues: (values: T[]) => void;
};

/**
 * Wraps a filter setter so an open entity drawer closes before a selection
 * that hides its entity is applied. The drawer's source node, and both
 * endpoints of the selected edge, are tested with `isNodeVisible` against the
 * values about to be written; `handleCloseDrawer` runs first when the verdict
 * is false, and only then does `onSetValues` write. An empty selection hides
 * nothing and skips the test.
 *
 * `handleCloseDrawer` rejects when the user declines the unsaved-changes
 * prompt, and the rejection propagates: the write never happens over a drawer
 * the user chose to keep. The canvas is read imperatively when the handler
 * runs, so holding this wrapper subscribes a component to nothing but the
 * drawer's source node.
 */
const useChangeFilterHandler = () => {
  const activeNode = useSourceNode();
  const { handleCloseDrawer } = useProject();

  return <ValuesType = string>({
      isNodeVisible,
      onSetValues,
    }: Options<ValuesType>) =>
    async (values: ValuesType[]) => {
      const { nodes, getActiveEdge } = canvasState();
      const activeEdge = getActiveEdge();

      if (activeNode && values.length && !isNodeVisible(values, activeNode)) {
        await handleCloseDrawer();
      }

      if (activeEdge) {
        const parentNode = nodes.find(
          (node) => node.id === activeEdge.data?.dto?.parent_id,
        );

        const childNode = nodes.find(
          (node) => node.id === activeEdge.data?.dto?.child_id,
        );

        const isEdgeVisible =
          !values.length ||
          (isNodeVisible(values, parentNode) &&
            isNodeVisible(values, childNode));

        if (!isEdgeVisible) {
          await handleCloseDrawer();
        }
      }

      onSetValues(values);
    };
};

export default useChangeFilterHandler;
